import {useLegacyConfig} from '@aboutyou/config';
import {useRequest} from '@aboutyou/data-fetcher';
import {GetSelfServiceFlowSectionResponse} from '../types'; // TODO: import from grpc once implemented https://aboutyou.atlassian.net/browse/AYFEP-2393
import {HIGHLIGHTED_SSF_ARTICLES} from '../utils/iris/constants';
import {createHelpCenterArticleUrl} from '../utils/iris/createHelpCenterArticleUrl';
import {
  ArticleFromIRIS,
  ArticlesFromIRIS,
  SectionsFromIRIS,
  fetchDataFromIRIS,
  getChildrenForSection,
  transformIrisSectionsToFlowSection,
} from '../utils/iris/iris';
import {
  useGetBreadcrumbsForSection,
  useGetContactUsCta,
  useGetSectionLevelTitle,
} from '../utils/iris/useGetTranslatedData';
import {useSectionIdFromUrl} from './useSectionIdFromUrl';

function useGetSelfServiceFlowBulk<
  Fragment extends keyof GetSelfServiceFlowSectionResponse,
>(key?: Fragment) {
  const legacyConfig = useLegacyConfig();
  const idFromUrl = useSectionIdFromUrl();
  const contactUsCta = useGetContactUsCta();
  const getBreadcrumbsForSection = useGetBreadcrumbsForSection();
  const getSectionLevelTitle = useGetSectionLevelTitle();

  const sectionId = idFromUrl === 0 ? undefined : idFromUrl;

  return useRequest<
    | GetSelfServiceFlowSectionResponse
    | Record<Fragment, GetSelfServiceFlowSectionResponse[Fragment]>
  >(
    `HELP_CENTER_SSF_SECTION-${sectionId ?? 'ROOT'}-${key ?? 'BULK'}`,
    async () => {
      const {sections} = await fetchDataFromIRIS<{sections: SectionsFromIRIS}>(
        '/sections',
        legacyConfig.help_center_GET,
      );

      const childrenSections = getChildrenForSection(sections, idFromUrl);
      const articlesData = await Promise.all(
        childrenSections.map(async section =>
          fetchDataFromIRIS<{articles: ArticlesFromIRIS; count: number}>(
            `/sections/${section.id}/articles`,
            legacyConfig.help_center_GET,
            {noScope: true},
          ),
        ),
      );
      const articles = articlesData.reduce(
        (articles, data) =>
          data.count > 0 ? [...articles, data.articles[0]!] : articles,
        [] as ArticlesFromIRIS,
      );

      const {section, isFirstLevel, isLastLevel} =
        transformIrisSectionsToFlowSection(sections, articles, sectionId);

      let highlightedArticles: GetSelfServiceFlowSectionResponse['highlightedArticles'] =
        [];

      // Get data to build existing articles, if necessary. If we're at level 1, we show the existing articles
      // for this section
      if (isFirstLevel) {
        const articlesResponses = await Promise.all(
          HIGHLIGHTED_SSF_ARTICLES.map(id =>
            fetchDataFromIRIS<{article: ArticleFromIRIS}>(
              `/articles/${id}`,
              legacyConfig.help_center_GET,
              {noScope: true},
            ),
          ),
        );

        highlightedArticles = articlesResponses.map(({article}) => ({
          label: article.name,
          linkTarget: {
            url: createHelpCenterArticleUrl(article.id),
            external: false,
          },
        }));
      }

      const contactUs = isLastLevel || isFirstLevel ? contactUsCta : undefined;
      const breadcrumbs = getBreadcrumbsForSection(sections, sectionId);
      const title = getSectionLevelTitle({isLastLevel, isFirstLevel});

      const response: GetSelfServiceFlowSectionResponse = {
        section,
        highlightedArticles,
        contactUs,
        breadcrumbs,
        title,
      };

      if (key == null) {
        return response;
      }

      return {[key as Fragment]: response[key]} as Record<
        Fragment,
        GetSelfServiceFlowSectionResponse[Fragment]
      >;
    },
  );
}

export function useGetSelfServiceFlowSection() {
  return useGetSelfServiceFlowBulk('section');
}

export function useGetSelfServiceFlowHighlightedArticles() {
  return useGetSelfServiceFlowBulk('highlightedArticles');
}

export function useGetSelfServiceFlowContactUs() {
  return useGetSelfServiceFlowBulk('contactUs');
}

export function useGetSelfServiceFlowBreadcrumbs() {
  return useGetSelfServiceFlowBulk('breadcrumbs');
}

export function useGetSelfServiceFlowLevelTitle() {
  return useGetSelfServiceFlowBulk('title');
}
